import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clientsAPI, usersAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Building2,
  Phone,
  Mail,
  MapPin,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  Layers,
  ChevronDown,
  ChevronRight,
  Landmark,
  Truck,
  Users,
  Briefcase,
  Check,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';

// Types de contact disponibles
const CONTACT_TYPES = [
  { value: 'client', label: 'Client', icon: Users, color: 'bg-blue-100 text-blue-700' },
  { value: 'snc', label: 'Cabinet SNC', icon: Briefcase, color: 'bg-purple-100 text-purple-700' },
  { value: 'banque', label: 'Banque', icon: Landmark, color: 'bg-green-100 text-green-700' },
  { value: 'fournisseur', label: 'Fournisseur', icon: Truck, color: 'bg-orange-100 text-orange-700' },
  { value: 'partenaire', label: 'Partenaire', icon: Building2, color: 'bg-slate-100 text-slate-700' },
];

const ClientsPage = ({ filterType = null }) => {
  const { isAdmin, isManager } = useAuth();
  const [searchParams] = useSearchParams();
  const urlFilterType = searchParams.get('type');
  const effectiveFilterType = filterType || urlFilterType || null;
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningClient, setAssigningClient] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  
  // Client profiles (catégories pour financement)
  const [clientProfiles, setClientProfiles] = useState([]);
  
  // Filtre par type de contact
  const [selectedContactType, setSelectedContactType] = useState(effectiveFilterType || 'all');
  
  // Grouping
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'category', 'city', 'vendeur'
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    notes: '',
    client_type: '',
    category: '',
    contact_types: ['client'], // Par défaut: client
  });

  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounce function for address search
  const debounceRef = useRef(null);

  const searchAddress = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setSearchingAddress(true);
    try {
      // Use French government address API (Base Adresse Nationale)
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      
      if (data.features) {
        const suggestions = data.features.map((feature) => ({
          label: feature.properties.label,
          name: feature.properties.name,
          city: feature.properties.city,
          postcode: feature.properties.postcode,
          street: feature.properties.street || feature.properties.name,
        }));
        setAddressSuggestions(suggestions);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Address search error:', error);
    } finally {
      setSearchingAddress(false);
    }
  }, []);

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, address: value });

    // Debounce the search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchAddress(value);
    }, 300);
  };

  const selectAddress = (suggestion) => {
    setForm({
      ...form,
      address: suggestion.street || suggestion.name,
      city: suggestion.city || '',
      postal_code: suggestion.postcode || '',
    });
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        addressInputRef.current &&
        !addressInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchClients();
    fetchClientProfiles();
    if (isAdmin() || isManager()) {
      fetchAssignableUsers();
    }
  }, [effectiveFilterType]);

  useEffect(() => {
    setSelectedContactType(effectiveFilterType || 'all');
  }, [effectiveFilterType]);

  const fetchClientProfiles = async () => {
    try {
      const response = await clientsAPI.getClientProfiles();
      setClientProfiles(response.data || []);
    } catch (error) {
      console.error('Error fetching client profiles:', error);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const response = await usersAPI.getAssignableForClients();
      setAssignableUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching assignable users:', error);
    }
  };

  const fetchClients = async () => {
    try {
      let response;
      if (effectiveFilterType) {
        response = await clientsAPI.getByContactType(effectiveFilterType);
      } else {
        response = await clientsAPI.getAll();
      }
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Au moins un type de contact obligatoire
    if (!form.contact_types || form.contact_types.length === 0) {
      toast.error('Veuillez sélectionner au moins un type de contact');
      return;
    }
    
    // Validation: Si "client" est coché, la catégorie est obligatoire
    if (form.contact_types.includes('client') && !form.category) {
      toast.error('Veuillez sélectionner une catégorie pour ce client (requise pour le financement SNC)');
      return;
    }
    
    setFormLoading(true);
    
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, form);
        toast.success('Contact mis à jour');
      } else {
        await clientsAPI.create(form);
        toast.success('Contact créé');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    // Conversion de l'ancien format contact_type (string) vers contact_types (array)
    let contactTypes = client.contact_types || [];
    if (!contactTypes.length && client.contact_type) {
      contactTypes = [client.contact_type];
    }
    if (!contactTypes.length) {
      contactTypes = ['client'];
    }
    
    setForm({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      address: client.address || '',
      city: client.city || '',
      postal_code: client.postal_code || '',
      country: client.country || 'France',
      notes: client.notes || '',
      client_type: client.client_type || '',
      category: client.category || '',
      contact_types: contactTypes,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    
    try {
      await clientsAPI.delete(clientId);
      toast.success('Client supprimé');
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openAssignDialog = (client) => {
    setAssigningClient(client);
    setSelectedAssignee(client.assigned_to || '');
    setAssignDialogOpen(true);
  };

  const handleAssignClient = async () => {
    if (!assigningClient || !selectedAssignee) {
      toast.error('Sélectionnez un responsable');
      return;
    }

    setAssignmentLoading(true);
    try {
      await clientsAPI.assign(assigningClient.id, selectedAssignee);
      toast.success('Client assigné');
      setAssignDialogOpen(false);
      setAssigningClient(null);
      setSelectedAssignee('');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l’assignation');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    // Si on est sur une page filtrée, pré-sélectionner ce type
    const defaultTypes = effectiveFilterType ? [effectiveFilterType] : ['client'];
    setForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'France',
      notes: '',
      client_type: '',
      category: '',
      contact_types: defaultTypes,
    });
  };
  
  // Toggle un type de contact dans la sélection
  const toggleContactType = (type) => {
    const current = form.contact_types || [];
    if (current.includes(type)) {
      // Ne pas permettre de tout décocher - garder au moins "client"
      if (current.length === 1) return;
      setForm({ ...form, contact_types: current.filter(t => t !== type) });
    } else {
      setForm({ ...form, contact_types: [...current, type] });
    }
  };

  // Helper pour obtenir les badges de types d'un contact
  const getContactTypeBadges = (client) => {
    const types = client.contact_types || (client.contact_type ? [client.contact_type] : ['client']);
    return types.map(type => {
      const typeInfo = CONTACT_TYPES.find(t => t.value === type) || CONTACT_TYPES[0];
      return (
        <Badge key={type} variant="outline" className={`text-xs ${typeInfo.color}`}>
          {typeInfo.label}
        </Badge>
      );
    });
  };

  // Filtrage par recherche ET par type de contact
  const filteredClients = clients.filter((client) => {
    // Filtre par recherche texte
    const matchesSearch = 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtre par type de contact
    let matchesType = true;
    if (selectedContactType !== 'all') {
      const clientTypes = client.contact_types || (client.contact_type ? [client.contact_type] : ['client']);
      matchesType = clientTypes.includes(selectedContactType);
    }
    
    return matchesSearch && matchesType;
  });

  // Grouping logic
  const groupedClients = React.useMemo(() => {
    if (groupBy === 'none') return null;
    
    const groups = {};
    filteredClients.forEach(client => {
      let groupKey;
      let groupLabel;
      
      switch (groupBy) {
        case 'category':
          groupKey = client.category || 'no-category';
          groupLabel = client.category || 'Non catégorisé';
          break;
        case 'city':
          groupKey = client.city || 'no-city';
          groupLabel = client.city || 'Ville non renseignée';
          break;
        case 'vendeur':
          groupKey = client.assigned_to || 'no-vendeur';
          groupLabel = client.assigned_to_name || 'Non assigné';
          break;
        default:
          groupKey = 'other';
          groupLabel = 'Autres';
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = { label: groupLabel, items: [] };
      }
      groups[groupKey].items.push(client);
    });
    
    return groups;
  }, [filteredClients, groupBy]);

  const toggleGroup = (groupKey) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  // Titre dynamique selon le filtre
  const getPageTitle = () => {
    if (!effectiveFilterType) return 'Contacts';
    const typeInfo = CONTACT_TYPES.find(t => t.value === effectiveFilterType);
    return typeInfo ? `${typeInfo.label}s` : 'Contacts';
  };

  const getPageSubtitle = () => {
    if (!effectiveFilterType) return `${clients.length} contacts enregistrés`;
    const typeInfo = CONTACT_TYPES.find(t => t.value === effectiveFilterType);
    return `${clients.length} ${typeInfo?.label?.toLowerCase() || 'contact'}${clients.length > 1 ? 's' : ''} enregistré${clients.length > 1 ? 's' : ''}`;
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            {getPageTitle()}
          </h1>
          <p className="text-slate-500 mt-1">{getPageSubtitle()}</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600" data-testid="new-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau contact
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                {editingClient ? 'Modifier le contact' : 'Nouveau contact'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company">Entreprise</Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    data-testid="client-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_type">Type (client/pro/collectivité)</Label>
                  <Select
                    value={form.client_type}
                    onValueChange={(value) => setForm({ ...form, client_type: value })}
                  >
                    <SelectTrigger data-testid="client-type-select">
                      <SelectValue placeholder="Sélectionner un type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="particulier">Particulier</SelectItem>
                      <SelectItem value="professionnel">Professionnel</SelectItem>
                      <SelectItem value="collectivite">Collectivité</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">
                  Catégorie (pour financement SNC)
                  {form.contact_types?.includes('client') && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Select
                  value={form.category}
                  onValueChange={(value) => setForm({ ...form, category: value })}
                >
                  <SelectTrigger 
                    data-testid="client-category-select"
                    className={form.contact_types?.includes('client') && !form.category ? 'border-red-300' : ''}
                  >
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientProfiles.filter(p => p.code !== 'banque').map((profile) => (
                      <SelectItem key={profile.code} value={profile.code}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: profile.color || '#3b82f6' }}
                          />
                          {profile.name}
                          {profile.required_documents?.length > 0 && (
                            <span className="text-xs text-slate-400 ml-1">
                              ({profile.required_documents.length} pièces)
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                    {clientProfiles.length === 0 && (
                      <>
                        <SelectItem value="agriculteur">Agriculteur</SelectItem>
                        <SelectItem value="professionnel">Professionnel</SelectItem>
                        <SelectItem value="particulier">Particulier</SelectItem>
                        <SelectItem value="collectivite">Collectivité</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                {form.contact_types?.includes('client') && !form.category && (
                  <p className="text-xs text-red-500">
                    Catégorie obligatoire pour les clients
                  </p>
                )}
                {form.category && (
                  <p className="text-xs text-slate-500">
                    {(() => {
                      const profile = clientProfiles.find(p => p.code === form.category);
                      if (profile?.required_documents?.length > 0) {
                        return `Pièces requises: ${profile.required_documents.slice(0, 3).join(', ')}${profile.required_documents.length > 3 ? '...' : ''}`;
                      }
                      return '';
                    })()}
                  </p>
                )}
              </div>
              <div className="space-y-3">
                <Label>
                  Types de contact (sélection multiple)
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                {(!form.contact_types || form.contact_types.length === 0) && (
                  <p className="text-xs text-red-500">Au moins un type obligatoire</p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {CONTACT_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isChecked = (form.contact_types || []).includes(type.value);
                    return (
                      <div
                        key={type.value}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${
                          isChecked ? 'border-yellow-400 bg-yellow-50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => toggleContactType(type.value)}
                        data-testid={`contact-type-${type.value}`}
                      >
                        <div className={`h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${
                          isChecked ? 'bg-slate-800 border-slate-800 text-white' : 'border-slate-300'
                        }`}>
                          {isChecked && <Check className="h-3 w-3" />}
                        </div>
                        <Icon className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{type.label}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  Un contact peut avoir plusieurs types. Les "Banques" apparaîtront dans les options de financement, les "SNC" dans le menu Cabinets SNC.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="client-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="client-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2 relative">
                <Label htmlFor="address">Adresse</Label>
                <div className="relative">
                  <Input
                    ref={addressInputRef}
                    id="address"
                    value={form.address}
                    onChange={handleAddressChange}
                    onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Commencez à taper une adresse..."
                    data-testid="client-address-input"
                    autoComplete="off"
                  />
                  {searchingAddress && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                  )}
                </div>
                {/* Address suggestions dropdown */}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto"
                  >
                    {addressSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 hover:bg-slate-100 cursor-pointer flex items-start gap-2"
                        onClick={() => selectAddress(suggestion)}
                      >
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-slate-800 truncate">{suggestion.label}</p>
                          <p className="text-xs text-slate-500">
                            {suggestion.postcode} {suggestion.city}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Tapez une adresse pour voir les suggestions (API Adresse France)
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Code postal</Label>
                  <Input
                    id="postal_code"
                    value={form.postal_code}
                    onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                    data-testid="client-postal-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ville</Label>
                  <Input
                    id="city"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    data-testid="client-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Pays</Label>
                  <Input
                    id="country"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    data-testid="client-country-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  data-testid="client-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-slate-800 hover:bg-slate-900"
                  disabled={formLoading}
                  data-testid="save-client-btn"
                >
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingClient ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Recherche */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-clients-input"
              />
            </div>
            
            {/* Filtre par type */}
            {!filterType && (
              <Select value={selectedContactType} onValueChange={setSelectedContactType}>
                <SelectTrigger className="w-[180px]">
                  <Users className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Type de contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {CONTACT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Grouper par */}
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[180px]">
                <Layers className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Grouper par..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Pas de groupe</SelectItem>
                <SelectItem value="category">Par catégorie</SelectItem>
                <SelectItem value="city">Par ville</SelectItem>
                <SelectItem value="vendeur">Par commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Compteur de résultats */}
          <div className="mt-3 text-sm text-slate-500">
            {filteredClients.length} contact{filteredClients.length > 1 ? 's' : ''} 
            {selectedContactType !== 'all' && ` de type "${CONTACT_TYPES.find(t => t.value === selectedContactType)?.label || selectedContactType}"`}
            {searchTerm && ` correspondant à "${searchTerm}"`}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {filteredClients.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm ? 'Aucun contact trouvé' : 'Aucun contact pour le moment'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Contact
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden sm:table-cell">
                      Types
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Email
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Téléphone
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">
                      Adresse
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">
                      Manager
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">
                      Vendeur
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">
                      Créé le
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupBy !== 'none' && groupedClients ? (
                    // Grouped view
                    Object.entries(groupedClients).map(([groupKey, group]) => (
                      <React.Fragment key={groupKey}>
                        {/* Group header row */}
                        <TableRow 
                          className="bg-slate-50 hover:bg-slate-100 cursor-pointer"
                          onClick={() => toggleGroup(groupKey)}
                        >
                          <TableCell colSpan={9} className="py-2">
                            <div className="flex items-center gap-2">
                              {collapsedGroups[groupKey] ? (
                                <ChevronRight className="w-4 h-4 text-slate-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-500" />
                              )}
                              <span className="font-semibold text-slate-700">{group.label}</span>
                              <Badge variant="outline" className="ml-2">{group.items.length}</Badge>
                            </div>
                          </TableCell>
                        </TableRow>
                        {/* Group items */}
                        {!collapsedGroups[groupKey] && group.items.map((client) => (
                          <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                            <TableCell>
                              <div className="pl-6">
                                <p className="font-medium text-slate-800">{client.name}</p>
                                {client.company && (
                                  <p className="text-sm text-slate-500 flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />
                                    <span className="truncate max-w-[120px] sm:max-w-none">{client.company}</span>
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <div className="flex flex-wrap gap-1">
                                {getContactTypeBadges(client)}
                              </div>
                            </TableCell>
                            <TableCell>
                              {client.email && (
                                <a href={`mailto:${client.email}`} className="text-sm text-slate-600 hover:text-blue-600 flex items-center gap-1">
                                  <Mail className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate max-w-[120px] sm:max-w-[180px]">{client.email}</span>
                                </a>
                              )}
                            </TableCell>
                            <TableCell>
                              {client.phone && (
                                <a href={`tel:${client.phone}`} className="text-sm text-slate-600 hover:text-blue-600 flex items-center gap-1">
                                  <Phone className="w-3 h-3 flex-shrink-0" />
                                  {client.phone}
                                </a>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              {client.address && (
                                <p className="text-sm text-slate-600 flex items-center gap-1">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate max-w-[150px]">{client.address}</span>
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                              {client.assigned_manager_name || '-'}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                              {client.assigned_to_name || '-'}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500 hidden lg:table-cell">
                              {formatDate(client.created_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                {(isAdmin() || isManager()) && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openAssignDialog(client)}>
                                    <Users className="w-4 h-4" />
                                  </Button>
                                )}
                                <Link to={`/clients/${client.id}`}>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </Link>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  ) : (
                    // Normal view
                    filteredClients.map((client) => (
                    <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-800">{client.name}</p>
                          {client.company && (
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              <span className="truncate max-w-[120px] sm:max-w-none">{client.company}</span>
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {getContactTypeBadges(client)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {client.email && (
                          <a href={`mailto:${client.email}`} className="text-sm text-slate-600 hover:text-blue-600 flex items-center gap-1">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate max-w-[120px] sm:max-w-[180px]">{client.email}</span>
                          </a>
                        )}
                      </TableCell>
                      <TableCell>
                        {client.phone && (
                          <a href={`tel:${client.phone}`} className="text-sm text-slate-600 hover:text-blue-600 flex items-center gap-1">
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            {client.phone}
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {client.address && (
                          <p className="text-sm text-slate-600 flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate max-w-[150px]">{client.address}</span>
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                        {client.assigned_manager_name || '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                        {client.assigned_to_name || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 hidden lg:table-cell">
                        {formatDate(client.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1 sm:gap-2">
                          {(isAdmin() || isManager()) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hidden sm:flex"
                              onClick={() => openAssignDialog(client)}
                              title="Assigner"
                            >
                              <Users className="w-4 h-4" />
                            </Button>
                          )}
                          <Link to={`/clients/${client.id}`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-client-${client.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hidden sm:flex"
                            onClick={() => handleEdit(client)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hidden sm:flex text-red-500 hover:text-red-600"
                            onClick={() => handleDelete(client.id)}
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={assignDialogOpen} onOpenChange={(open) => {
        setAssignDialogOpen(open);
        if (!open) {
          setAssigningClient(null);
          setSelectedAssignee('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              Assigner le contact
            </DialogTitle>
          </DialogHeader>
          {assigningClient && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                <span className="font-medium text-slate-800">{assigningClient.name}</span>
                {assigningClient.company ? ` • ${assigningClient.company}` : ''}
              </div>
              <div className="space-y-2">
                <Label>Responsable</Label>
                <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un manager ou un vendeur" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} - {user.role === 'manager' ? 'Manager' : `Vendeur${user.manager_name ? ` (${user.manager_name})` : ''}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">
                Un client affecté à un manager reste dans son équipe. Le manager peut ensuite le réassigner à un vendeur de son équipe.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={handleAssignClient}
                  className="bg-slate-800 hover:bg-slate-900"
                  disabled={assignmentLoading || !selectedAssignee}
                >
                  {assignmentLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Assigner
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
